import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, InputAdornment, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Paper, Chip, Checkbox,
  CircularProgress, Alert, Tooltip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ErrorIcon from '@mui/icons-material/Error';
import BlockIcon from '@mui/icons-material/Block';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useMsal } from '@azure/msal-react';
import { authFetch } from '../../utils/authHelpers';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import { AI_ENGINE_URL } from '../../utils/apiConfig';
import VideoDetailDialog from './VideoDetailDialog';
import { useEigenThumb } from './eigenThumb';

const API_BASE = `${AI_ENGINE_URL}/api/v1/video-search`;

function KleineThumb({ video }) {
  const src = useEigenThumb(video.id, video.thumb_small, video.thumb_maken);
  return src ? (
    <Box
      component="img"
      src={src}
      alt=""
      sx={{ width: 48, height: 32, objectFit: 'cover', borderRadius: 0.5, display: 'block' }}
      onError={(e) => { e.target.style.display = 'none'; }}
    />
  ) : (
    <Box sx={{ width: 48, height: 32, bgcolor: 'grey.200', borderRadius: 0.5 }} />
  );
}

const ADMIN_EMAILS = ['joris@fullframe.nl', 'ton@fullframe.nl'];

const STATUS_CONFIG = {
  completed: { label: 'Geanalyseerd', color: 'success', icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
  pending: { label: 'Wacht', color: 'warning', icon: <HourglassEmptyIcon sx={{ fontSize: 14 }} /> },
  processing: { label: 'Bezig...', color: 'info', icon: <CircularProgress size={12} /> },
  failed: { label: 'Mislukt', color: 'error', icon: <ErrorIcon sx={{ fontSize: 14 }} /> },
  skipped: { label: 'Overgeslagen', color: 'default', icon: <BlockIcon sx={{ fontSize: 14 }} /> },
  unprocessed: { label: 'Nieuw', color: 'primary', icon: <FiberNewIcon sx={{ fontSize: 14 }} /> },
};

const formatDuration = (seconds) => {
  if (!seconds) return '-';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const estimateCostEur = (videos) => {
  let totalUsd = 0;
  for (const v of videos) {
    const dur = v.duration || 60; // default 1 min if unknown
    const audioCost = (dur / 60) * 0.003;
    const visualCost = (dur / 5) * 0.001;
    totalUsd += audioCost + visualCost;
  }
  return totalUsd * 0.92; // USD to EUR
};

export default function VideoManagement() {
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const userEmail = activeAccount?.username?.toLowerCase() || '';
  const isAdmin = ADMIN_EMAILS.includes(userEmail);

  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalFiltered, setTotalFiltered] = useState(0);
  const [counts, setCounts] = useState({});
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Multi-select
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Cost estimation dialog
  const [costDialogOpen, setCostDialogOpen] = useState(false);
  const [bulkProcessing, setBulkProcessing] = useState(false);

  const fetchVideos = useCallback(async (p, ps, s, sf) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: (p + 1).toString(),
        page_size: ps.toString(),
        status: sf,
      });
      if (s) params.set('search', s);

      const resp = await authFetch(`${API_BASE}/videos?${params}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();

      setVideos(data.videos || []);
      setTotalFiltered(data.total_filtered || 0);
      setCounts(data.counts || {});
    } catch (e) {
      setError('Laden mislukt: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVideos(page, pageSize, search, statusFilter);
  }, [page, pageSize, statusFilter, fetchVideos]);

  // Clear selection when page/filter changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, pageSize, statusFilter]);

  // Ctrl+Z / Cmd+Z buiten tekstvelden = laatste actie terugdraaien
  useEffect(() => registreerOngedaanSneltoets(), []);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchTimeout(setTimeout(() => {
      setPage(0);
      fetchVideos(0, pageSize, val, statusFilter);
    }, 400));
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setPage(0);
  };

  const handleRowClick = (video) => {
    if (video.analysis_status === 'completed') {
      setSelectedVideoId(video.id);
      setDetailOpen(true);
    }
  };

  // Checkbox handlers
  const handleToggleSelect = (videoId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(videoId)) {
        next.delete(videoId);
      } else {
        next.add(videoId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === videos.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(videos.map(v => v.id)));
    }
  };

  // Bulk skip/unskip
  const handleBulkSkip = async (skip, ids = [...selectedIds], { stil = false } = {}) => {
    try {
      const resp = await authFetch(`${API_BASE}/videos/bulk-skip`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, skip }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      await resp.json();
      setSelectedIds(new Set());
      fetchVideos(page, pageSize, search, statusFilter);
      if (!stil && ids.length > 0) {
        // Ongedaan maken = de omgekeerde bulk-actie voor dezelfde ids
        biedOngedaanAan(
          `${ids.length} video's ${skip ? 'overgeslagen' : 'niet meer overgeslagen'}`,
          () => handleBulkSkip(!skip, ids, { stil: true }),
        );
      }
    } catch (e) {
      if (stil) throw e;
      setError(`Bulk ${skip ? 'skip' : 'unskip'} mislukt: ` + e.message);
    }
  };

  // Bulk analyze
  const selectedVideos = videos.filter(v => selectedIds.has(v.id));
  const analyzableSelected = selectedVideos.filter(v => v.analysis_status !== 'completed');
  const estimatedCost = estimateCostEur(analyzableSelected);
  const costExceedsLimit = estimatedCost > 5;

  const handleOpenCostDialog = () => {
    setCostDialogOpen(true);
  };

  const handleStartBulkAnalysis = async () => {
    setCostDialogOpen(false);
    setBulkProcessing(true);
    const ids = analyzableSelected.map(v => v.id);
    try {
      // Unskip any skipped videos first
      const skippedIds = analyzableSelected.filter(v => v.skip_analysis).map(v => v.id);
      if (skippedIds.length > 0) {
        await authFetch(`${API_BASE}/videos/bulk-skip`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ids: skippedIds, skip: false }),
        });
      }
      const resp = await authFetch(`${API_BASE}/process/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setSelectedIds(new Set());
      // Poll briefly to show status update
      setTimeout(() => fetchVideos(page, pageSize, search, statusFilter), 2000);
    } catch (e) {
      setError('Bulk analyse mislukt: ' + e.message);
    } finally {
      setBulkProcessing(false);
    }
  };

  const filterChips = [
    { key: 'all', label: `Alle (${counts.total || 0})` },
    { key: 'completed', label: `Geanalyseerd (${counts.completed || 0})` },
    { key: 'unprocessed', label: `Nieuw (${counts.unprocessed || 0})` },
    { key: 'skipped', label: `Overgeslagen (${counts.skipped || 0})` },
  ];

  return (
    <Box>
      {/* Filter chips */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {filterChips.map(fc => (
          <Chip
            key={fc.key}
            label={fc.label}
            onClick={() => handleStatusFilter(fc.key)}
            variant={statusFilter === fc.key ? 'filled' : 'outlined'}
            color={statusFilter === fc.key ? 'primary' : 'default'}
            size="small"
          />
        ))}
      </Box>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <Paper
          elevation={2}
          sx={{
            display: 'flex', gap: 1.5, mb: 2, p: 1.5, alignItems: 'center',
            bgcolor: 'primary.50', borderRadius: 2, border: '1px solid',
            borderColor: 'primary.200',
          }}
        >
          <Typography variant="body2" fontWeight={600} sx={{ mr: 1 }}>
            {selectedIds.size} geselecteerd
          </Typography>
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={bulkProcessing ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
            disabled={analyzableSelected.length === 0 || analyzableSelected.length > 50 || bulkProcessing}
            onClick={handleOpenCostDialog}
          >
            Analyseren ({analyzableSelected.length}{analyzableSelected.length > 50 ? ' - max 50!' : ''})
          </Button>
          {selectedVideos.some(v => !v.skip_analysis) && (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<VisibilityOffIcon />}
              onClick={() => handleBulkSkip(true)}
            >
              Negeren
            </Button>
          )}
          {selectedVideos.some(v => v.skip_analysis) && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={() => handleBulkSkip(false)}
            >
              Terugzetten
            </Button>
          )}
          <Button
            size="small"
            variant="text"
            color="inherit"
            onClick={() => setSelectedIds(new Set())}
            sx={{ ml: 'auto' }}
          >
            Deselecteer
          </Button>
        </Paper>
      )}

      {/* Search */}
      <TextField
        fullWidth
        size="small"
        placeholder="Zoek op bestandsnaam, projectnummer of klant..."
        value={search}
        onChange={handleSearchChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  indeterminate={selectedIds.size > 0 && selectedIds.size < videos.length}
                  checked={videos.length > 0 && selectedIds.size === videos.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell sx={{ width: 50 }}></TableCell>
              <TableCell sx={{ minWidth: 400 }}>Bestandsnaam</TableCell>
              <TableCell sx={{ width: 100 }}>Klant</TableCell>
              <TableCell sx={{ width: 80 }}>Project</TableCell>
              <TableCell sx={{ width: 100 }}>Categorie</TableCell>
              <TableCell align="right">Duur</TableCell>
              <TableCell align="center">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && videos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : videos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Geen video's gevonden
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              videos.map((video) => {
                const statusCfg = STATUS_CONFIG[video.analysis_status] || STATUS_CONFIG.unprocessed;
                const isClickable = video.analysis_status === 'completed';
                const isSelected = selectedIds.has(video.id);
                return (
                  <TableRow
                    key={video.id}
                    hover
                    selected={isSelected}
                    sx={{
                      cursor: isClickable ? 'pointer' : 'default',
                      opacity: video.skip_analysis ? 0.5 : 1,
                    }}
                    onClick={() => handleRowClick(video)}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        size="small"
                        checked={isSelected}
                        onChange={() => handleToggleSelect(video.id)}
                      />
                    </TableCell>
                    <TableCell sx={{ p: 0.5 }}>
                      <KleineThumb video={video} />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={video.filename} placement="top-start">
                        <Typography variant="body2" noWrap sx={{ maxWidth: 500, fontSize: '0.8rem' }}>
                          {video.filename}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 150, fontSize: '0.8rem', color: 'text.secondary' }}>
                        {video.client_name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                        {video.project_number || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {video.category ? (
                        <Chip label={video.category} size="small" variant="outlined" color="secondary" sx={{ fontSize: '0.65rem', height: 20 }} />
                      ) : (
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>-</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                        {formatDuration(video.duration)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={statusCfg.icon}
                        label={statusCfg.label}
                        size="small"
                        color={statusCfg.color}
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: 22 }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalFiltered}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[25, 50, 100]}
        labelRowsPerPage="Per pagina:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} van ${count}`}
      />

      {/* Cost Estimation Dialog */}
      <Dialog open={costDialogOpen} onClose={() => setCostDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Analyse starten</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>{analyzableSelected.length}</strong> video's geselecteerd voor analyse
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Geschatte kosten: <strong>{'\u20AC'}{estimatedCost.toFixed(2)}</strong>
          </Typography>
          {costExceedsLimit && !isAdmin && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              Kosten boven {'\u20AC'}5 - alleen admin mag dit starten.
              Neem contact op met Joris of Ton.
            </Alert>
          )}
          {costExceedsLimit && isAdmin && (
            <Alert severity="info" sx={{ mb: 1 }}>
              Kosten boven {'\u20AC'}5 - je bent ingelogd als admin.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCostDialogOpen(false)}>Annuleren</Button>
          <Button
            variant="contained"
            onClick={handleStartBulkAnalysis}
            disabled={costExceedsLimit && !isAdmin}
          >
            Start analyse
          </Button>
        </DialogActions>
      </Dialog>

      <VideoDetailDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        videoId={selectedVideoId}
      />
    </Box>
  );
}
